import { ChangeEvent, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isValid, parse } from 'date-fns';
import { Button, Calendar, DateRange, Label, Popover, TextInput } from '@c2fo/liquidity';
import { parseDate } from '@/enterprise/features/history/utils';
import useLocaleFormat from '@/utils/useLocaleFormat';

// Function to validate a date string in the format of yyyy-MM-dd (This is the format that the API requires)
const isValidDateString = (dateString: string): boolean => {
  // First, check if the string matches the exact format yyyy-MM-dd (4 digits, 2 digits, 2 digits)
  const formatRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!formatRegex.test(dateString)) {
    return false;
  }

  // Then, check if it's a valid date (e.g., not 2025-13-01 or 2025-02-30)
  const parsedDate = parse(dateString, 'yyyy-MM-dd', new Date());
  return isValid(parsedDate);
};

// Function to validate the selected "from" and "to" range
const isValidDateRange = ({ from, to }: InputRange) => {
  if (!from || !to) {
    return false;
  }

  // Validate "from" and "to" dates are both able to be parsed as valid dates in the format of yyyy-MM-dd
  const validDate = from && isValidDateString(from) && to && isValidDateString(to);

  // Validate the "from" date is less than or equal to the "to" date
  const fromDate = parse(from, 'yyyy-MM-dd', new Date());
  const toDate = parse(to, 'yyyy-MM-dd', new Date());
  const validRange = fromDate <= toDate;

  return !!(validDate && validRange);
};

// A half-typed input (e.g. "2026-10-0") parses to an Invalid Date. react-day-picker throws "Invalid range"
// on one, and clears the inputs when only one end is valid, so the calendar gets no range at all instead.
const isInvalidDate = (date: Date | undefined): boolean => date !== undefined && Number.isNaN(date.getTime());

interface StringDateRange {
  startDate: string;
  endDate: string;
}

interface InputRange {
  from: string;
  to: string;
}

export interface RangePickerProps {
  /**
   * The class name to apply to the root element.
   */
  className?: string;
  /**
   * The value of the date range.
   */
  value: StringDateRange;
  /**
   * Event fired when the date range changes.
   */
  onChange: (range: StringDateRange | undefined) => void;
  /**
   * Whether the popover is open. This is controlled external
   * to allow for it to be programmatically closed in certain scenarios.
   */
  open?: boolean;
  /**
   * Event fired when the popover is opened or closed.
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Element used for triggering the RangerPicker. Defaults to "Custom Range".
   */
  trigger?: ReactNode;
}

/**
 * A component that allows the user to select a custom date range.
 */
export default function RangePicker({ value, onChange, className, open, onOpenChange, trigger }: RangePickerProps) {
  // NOTE: react-day-picker defines ranges with from/to properties.
  // Our legacy code uses startDate/endDate. This component
  // is a bridge between the two.
  const [dateRange, setDateRange] = useState<InputRange>({
    from: value.startDate,
    to: value.endDate,
  });
  const [isValid, setIsValid] = useState(false);
  const { t } = useTranslation();

  const from = dateRange.from ? parseDate(dateRange.from) : undefined;
  const to = dateRange.to ? parseDate(dateRange.to) : undefined;
  const calendarRange = isInvalidDate(from) || isInvalidDate(to) ? undefined : { from, to };

  // Update the date range values when the outside value changes
  useEffect(() => {
    setDateRange({ from: value.startDate, to: value.endDate });
  }, [value]);

  // Validates the text date range whenever it changes
  useEffect(() => {
    setIsValid(isValidDateRange(dateRange));
  }, [dateRange]);

  const { asISODateString } = useLocaleFormat();
  const handleInputChange = (type: keyof DateRange) => (e: ChangeEvent<HTMLInputElement>) => {
    const dateValue = e.currentTarget.value;
    // Update the date range state for from and to text components
    setDateRange((prevState) => ({
      ...prevState,
      [type]: dateValue,
    }));
  };

  const handleRangeSelect = (range: DateRange | undefined) => {
    // Update the "from" and "to" date range based on the dates selected in Calendar component
    const from = range?.from ? asISODateString(range.from) : '';
    const to = range?.to ? asISODateString(range.to) : '';

    setDateRange({
      from,
      to,
    });
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <Popover.Trigger className={className}>
        {trigger ?? <span className="capitalize">{t('core.customRange')}</span>}
      </Popover.Trigger>
      <Popover.Content align="start" className="p-4" sideOffset={5} variant="light">
        <div className="flex-col: flex flex-nowrap gap-2 sm:flex-row">
          <div className="flex flex-col gap-3">
            <div className="space-y-1">
              <Label htmlFor="from" className="capitalize">
                {t('core.from')}
              </Label>
              <TextInput
                id="from"
                name="from"
                onChange={handleInputChange('from')}
                pattern="\d{4}-\d{2}-\d{2}" // Pattern for yyyy-MM-dd - This is not currently enforcing the pattern because this is not a form submit
                size="sm"
                type="text"
                value={dateRange.from}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="to" className="capitalize">
                {t('core.to')}
              </Label>
              <TextInput
                id="to"
                name="to"
                onChange={handleInputChange('to')}
                pattern="\d{4}-\d{2}-\d{2}" // Pattern for yyyy-MM-dd - This is not currently enforcing the pattern because this is not a form submit
                size="sm"
                type="text"
                value={dateRange.to}
              />
            </div>
            <div className="flex justify-start gap-2">
              <Button variant="secondary" size="sm" type="button" onClick={() => onOpenChange?.(false)}>
                {t('core.cancel')}
              </Button>
              <Button
                variant="primary"
                size="sm"
                type="button"
                disabled={!isValid}
                onClick={() => {
                  onChange({
                    startDate: dateRange.from ? asISODateString(dateRange.from) : '',
                    endDate: dateRange.to ? asISODateString(dateRange.to) : '',
                  });
                  onOpenChange?.(false);
                }}
              >
                {t('core.apply')}
              </Button>
            </div>
          </div>
          <div>
            <Calendar
              mode="range"
              numberOfMonths={2}
              onSelect={handleRangeSelect}
              pagedNavigation
              selected={calendarRange}
            />
          </div>
        </div>
      </Popover.Content>
    </Popover>
  );
}
